// ------------------------------------------------------------------
// Presell VARIANT: FABLE
// Faithful port of presell-mockups/presell-FABLE.html.
// DNA: editorial spec-sheet. Red top-tick control cards, hairline
// borders, black (not blue) CTA bands, inline SVG icons (NO emojis).
// Section order matches the original mockup exactly:
//   hero -> channels -> control -> how -> pricing(table+value) ->
//   facebook -> search journey -> pains -> resources -> dashboard ->
//   faq -> haul star close -> final CTA.
// Core marketing COPY comes from shared presellContent (A/B integrity);
// Fable-specific structural content lives in FABLE_* exports there.
// ------------------------------------------------------------------

import React from 'react';
import { func } from 'prop-types';

import heroImg from '../../assets/presell/hero-large.webp';
import dumpImg from '../../assets/presell/dump-trailer.jpg';
import carHaulerImg from '../../assets/presell/car-hauler.jpg';
import logoInverted from '../../assets/presell/logo-clean-inverted.png';

import {
  TABLE_ROWS,
  FAQ,
  FABLE_CHANNELS,
  FABLE_CONTROL,
  FABLE_STEPS,
  FABLE_JOURNEY,
  FABLE_PAINS,
  FABLE_GUIDES,
  FABLE_RESOURCE_CHIPS,
  FABLE_STATS,
  FABLE,
  COPY,
} from './presellContent';
import css from './FableLayout.module.css';

const RED = '#E31E24';
const GREEN = '#22A745';
const GUIDE_IMG = { carHauler: carHaulerImg, dump: dumpImg };

// ── Inline SVG icons (faithful to the mockup, no emojis) ──────────
const Check = ({ stroke = GREEN, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3 8.5l3.2 3.2L13 5" stroke={stroke} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChannelIcon = ({ name }) => {
  switch (name) {
    case 'facebook':
      return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M15 8h3V4.5h-3c-2 0-3.5 1.6-3.5 3.5v2.5H9V14h2.5v6H15v-6h2.6l.4-3.5h-3V8z" fill={RED} />
        </svg>
      );
    case 'google':
      return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="10.5" cy="10.5" r="6" stroke={RED} strokeWidth="2.4" />
          <path d="M15 15l5 5" stroke={RED} strokeWidth="2.4" strokeLinecap="round" />
        </svg>
      );
    case 'word':
      return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="9" cy="8" r="3.2" stroke={RED} strokeWidth="2.2" />
          <circle cx="16.5" cy="9.5" r="2.4" stroke={RED} strokeWidth="2.2" />
          <path d="M3.5 19c.6-3 3-4.5 5.5-4.5s4.9 1.5 5.5 4.5" stroke={RED} strokeWidth="2.2" strokeLinecap="round" />
        </svg>
      );
    case 'website':
    default:
      return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect x="3" y="4" width="18" height="13" rx="1.6" stroke={RED} strokeWidth="2.2" />
          <path d="M8 21h8" stroke={RED} strokeWidth="2.2" strokeLinecap="round" />
        </svg>
      );
  }
};

const PainIcon = ({ name }) => {
  switch (name) {
    case 'damage':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M12 3l9 16H3l9-16z" stroke={RED} strokeWidth="2" />
          <path d="M12 10v4" stroke={RED} strokeWidth="2" strokeLinecap="round" />
          <circle cx="12" cy="17" r="1.2" fill={RED} />
        </svg>
      );
    case 'tire':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="8" stroke={RED} strokeWidth="2" />
          <circle cx="12" cy="12" r="3" stroke={RED} strokeWidth="2" />
        </svg>
      );
    case 'theft':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M12 3l7 3v5c0 4.5-3 8.2-7 10-4-1.8-7-5.5-7-10V6l7-3z" stroke={RED} strokeWidth="2" strokeLinejoin="round" />
        </svg>
      );
    case 'pricing':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M12 3v18M7 8c0-2 2.2-3 5-3s5 1 5 3-2.2 3-5 3-5 1-5 3 2.2 3 5 3 5-1 5-3" stroke={RED} strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    case 'renters':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="8" r="3.5" stroke={RED} strokeWidth="2" />
          <path d="M5 20c.7-3.5 3.5-5.5 7-5.5s6.3 2 7 5.5" stroke={RED} strokeWidth="2" strokeLinecap="round" />
          <path d="M18 4l3 3M21 4l-3 3" stroke={RED} strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    case 'growth':
    default:
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M4 20V10M10 20V4M16 20v-8M21 20H3" stroke={RED} strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
  }
};

const MagIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="10.5" cy="10.5" r="6" stroke="#888" strokeWidth="2.2" />
    <path d="M15 15l5 5" stroke="#888" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

const PhoneGlyph = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true" className={css.phoneGlyph}>
    <path
      d="M6.5 3h3l1.4 4-2 1.4a12 12 0 0 0 5.2 5.2l1.4-2 4 1.4v3a2 2 0 0 1-2.2 2A16 16 0 0 1 4.5 5.2 2 2 0 0 1 6.5 3z"
      fill="currentColor"
    />
  </svg>
);

const FableLayout = ({ listFreeLink, haulStarLink, newsletterLink }) => (
  <div className={css.root}>

    {/* ── 1. HERO ─────────────────────────────────── */}
    <section className={css.hero} id="top">
      {/* Mobile-only: hero image as a full-bleed background behind the copy so
          it lands above the fold. Desktop keeps the side-by-side .heroImg.
          Inline style is required — the webp is a webpack-hashed asset that
          can't be referenced from the .module.css (BigLoads build lesson). */}
      <div
        className={css.heroBgMobile}
        style={{ backgroundImage: `url(${heroImg})` }}
        aria-hidden="true"
      />
      <div className={css.heroBgOverlay} aria-hidden="true" />
      <div className={css.wrap}>
        <div className={css.heroGrid}>
          <div className={css.heroCopy}>
            <span className={css.kicker}>{COPY.eyebrowHero}</span>
            <h1 className={css.h1}>
              Put Your Trailers in Front of <span className={css.u}>More Renters.</span>
            </h1>
            <p className={css.heroSub}>
              <strong className={css.ink}>Keep your rates. Keep your customers. Run your rentals your way.</strong>{' '}
              BigLoads helps people searching for trailers in your area discover your fleet. List free, then
              upgrade to Haul Star when you want renters to contact your business directly.
            </p>
            <div className={css.btnRow}>
              {listFreeLink(`${css.btn} ${css.btnRed} cta-list-free`, 'hero', COPY.ctaListFree)}
              <a href="#how" className={`${css.btn} ${css.btnGhost}`}>{COPY.ctaHowItWorks}</a>
            </div>
            <div className={css.microtrust}>
              {FABLE.microtrust.map(m => (
                <span key={m}><Check /> {m}</span>
              ))}
            </div>
          </div>
          <div className={css.heroImg}>
            <img src={heroImg} alt="Trailers ready to rent in a trailer yard" loading="eager" />
            <div className={css.heroTag}>
              More renters. More hauls. <em>{FABLE.heroBadgeEm}</em>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ── 2. DISTRIBUTION CHANNELS ────────────────── */}
    <section className={css.channels}>
      <div className={css.wrap}>
        <div className={css.center}>
          <span className={css.kicker}>{FABLE.channelsEyebrow}</span>
          <h2 className={css.h2}>
            {FABLE.channelsHa}<br />{FABLE.channelsHb}
          </h2>
        </div>
        <div className={css.chanGrid}>
          {FABLE_CHANNELS.map(c => (
            <div className={css.chan} key={c.h}>
              <div className={css.chanIc}><ChannelIcon name={c.ic} /></div>
              <h4 className={css.chanH}>{c.h}</h4>
              <p className={css.chanP}>{c.p}</p>
            </div>
          ))}
          <div className={css.chanPlus}>+</div>
          <div className={css.chanBl}>
            <img src={logoInverted} alt="BigLoads" className={css.chanBlLogo} />
            <strong>{FABLE.channelsBlStrong}</strong>
            <p>{FABLE.channelsBlP}</p>
          </div>
        </div>
        <p className={css.chanNote}>{FABLE.channelsNote}</p>
      </div>
    </section>

    {/* ── 3. CONTROL — red top-tick cards ─────────── */}
    <section className={css.sec}>
      <div className={css.wrap}>
        <div className={css.center}>
          <span className={css.kicker}>{FABLE.controlEyebrow}</span>
          <h2 className={css.h2}>
            {FABLE.controlHa}<br />{FABLE.controlHb}
          </h2>
        </div>
        <div className={css.ctlGrid}>
          {FABLE_CONTROL.map(c => (
            <div className={css.ctl} key={c.num}>
              <span className={css.ctlNum}>{c.num}</span>
              <h3 className={css.h3}>{c.h}</h3>
              <p className={css.ctlP}>{c.p}</p>
            </div>
          ))}
        </div>
        <p className={css.painTail}>{FABLE.controlNote}</p>
      </div>
    </section>

    {/* ── 4. HOW IT WORKS + proof ─────────────────── */}
    <section className={css.how} id="how">
      <div className={css.wrap}>
        <div className={css.center}>
          <span className={css.kicker}>{FABLE.howEyebrow}</span>
          <h2 className={css.h2}>{FABLE.howH}</h2>
        </div>
        <div className={css.howGrid}>
          {FABLE_STEPS.map(s => (
            <div className={css.step} key={s.n}>
              <div className={css.stepN}>{s.n}</div>
              <h3 className={css.h3}>{s.h}</h3>
              <p className={css.stepP}>{s.p}</p>
            </div>
          ))}
        </div>
        <div className={css.howProof}>
          <p>
            {FABLE.howProofA}<em>{FABLE.howProofEm}</em>{FABLE.howProofB}
          </p>
          {listFreeLink(`${css.btn} ${css.btnRed} cta-list-free`, 'how', COPY.ctaListFree)}
        </div>
        <div className={css.proofStat}>
          <p className={css.proofStatBig}>
            {FABLE.proofStatA}<span className={css.red}>{FABLE.proofStatRed}</span>{FABLE.proofStatB}{' '}
            <span className={css.proofStatSmall}>{FABLE.proofStatSmall}</span>
          </p>
          <p className={css.proofStatNote}>{FABLE.proofStatNote}</p>
        </div>
      </div>
    </section>

    {/* ── 5+6. PRICING (table LEFT + value math RIGHT) ── */}
    <section className={css.sec} id="pricing">
      <div className={css.wrap}>
        <div className={css.center}>
          <span className={css.kicker}>{FABLE.pricingEyebrow}</span>
          <h2 className={css.h2}>{FABLE.pricingH}</h2>
          <p className={css.lede}>{FABLE.pricingLede}</p>
        </div>
        <div className={css.cmpWrap}>
          <div>
            <table className={css.cmp}>
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Free Listing</th>
                  <th className={css.hs}>
                    Haul Star<small>Most Popular</small>
                  </th>
                </tr>
              </thead>
              <tbody>
                {TABLE_ROWS.map(r => (
                  <tr key={r.f}>
                    <td>{r.f}</td>
                    <td>
                      {r.free === true ? (
                        <span className={css.yes}><Check size={15} /></span>
                      ) : r.free === false ? (
                        <span className={css.no}>&ndash;</span>
                      ) : (
                        r.free
                      )}
                    </td>
                    <td className={css.hs}>
                      {r.hs === true ? (
                        <span className={css.yes}><Check size={15} /></span>
                      ) : (
                        r.hs
                      )}
                    </td>
                  </tr>
                ))}
                <tr className={css.priceRow}>
                  <td>Price</td>
                  <td>$0</td>
                  <td className={`${css.hs} ${css.priceHs}`}>$30/mo</td>
                </tr>
              </tbody>
            </table>
            <p className={css.cmpNote}>
              <strong>{FABLE.tableNote}</strong>{FABLE.tableNoteRest}
            </p>
            <div className={css.cmpCta}>
              {haulStarLink(`${css.btn} ${css.btnRed} cta-haul-star`, 'pricing', COPY.ctaBecomeHaulStar)}
              {listFreeLink(`${css.btn} ${css.btnGhost} cta-list-free`, 'pricing', 'Start With a Free Listing')}
            </div>
          </div>
          <div className={css.valCard}>
            <h3 className={css.valH}>{FABLE.valueH}</h3>
            <p className={css.valP}>{FABLE.valueP}</p>
            <div className={css.valBox}>
              <div className={css.valPrice}>$30<small>per month</small></div>
              <ul>
                {FABLE.valueBoxList.map(v => <li key={v}>{v}</li>)}
              </ul>
            </div>
            <p className={css.valTail}>{FABLE.valueTail}</p>
          </div>
        </div>
      </div>
    </section>

    {/* ── 7. FACEBOOK SHELF LIFE ──────────────────── */}
    <section className={css.fb}>
      <div className={`${css.wrap} ${css.fbGrid}`}>
        <div>
          <span className={css.kicker}>{FABLE.fbEyebrow}</span>
          <h2 className={css.h2}>{FABLE.fbH}</h2>
          <p className={css.lede}>{FABLE.fbLede}</p>
          <p className={css.fbP2}>{FABLE.fbP2}</p>
        </div>
        <div>
          <div className={css.listingCard}>
            <img src={dumpImg} alt="7x14 dump trailer listing on BigLoads" loading="lazy" />
            <div className={css.lcBody}>
              <h4 className={css.lcH}>{FABLE.fbCardH}</h4>
              <p className={css.lcBiz}>{FABLE.fbCardBiz}</p>
              <p className={css.lcLoc}>{FABLE.fbCardLoc}</p>
              <div className={css.lcMeta}>
                {FABLE.fbCardMeta.map(m => <span key={m}>{m}</span>)}
              </div>
              {listFreeLink(
                `${css.lcBtn} cta-list-free`,
                'demo-call-owner',
                <><PhoneGlyph />&nbsp; Call Owner</>
              )}
            </div>
          </div>
          <p className={css.lcCap}>{FABLE.fbCap}</p>
        </div>
      </div>
    </section>

    {/* ── 8. SEARCH INTENT JOURNEY ────────────────── */}
    <section className={css.sec}>
      <div className={css.wrap}>
        <div className={css.center}>
          <span className={css.kicker}>{FABLE.searchEyebrow}</span>
          <h2 className={css.h2}>{FABLE.searchH}</h2>
          <p className={css.lede}>{FABLE.searchLede}</p>
        </div>
        <div className={css.journey}>
          {FABLE_JOURNEY.map((j, i) => (
            <React.Fragment key={j.tag}>
              {i > 0 && <div className={css.jArrow}>&rarr;</div>}
              <div className={css.jStep}>
                <span className={css.jTag}>{j.tag}</span>
                {j.kind === 'search' && (
                  <div className={css.jSearch}><MagIcon />{j.query}</div>
                )}
                {j.kind === 'result' && (
                  <div className={css.jResult}>
                    <p className={css.jUrl}>{j.url}</p>
                    <p className={css.jTitle}>{j.title}</p>
                    <p className={css.jDesc}>{j.desc}</p>
                  </div>
                )}
                {j.kind === 'call' && (
                  <div className={css.jCall}>
                    <p>{j.biz}</p>
                    {listFreeLink(
                      `${css.jBtnMini} cta-list-free`,
                      'demo-call-owner',
                      <><PhoneGlyph /> Call Owner</>
                    )}
                  </div>
                )}
                <p className={css.jP}>{j.p}</p>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>

    {/* ── 9. BUILT FOR OWNERS (pains) ─────────────── */}
    <section className={css.built}>
      <div className={css.wrap}>
        <div className={css.center}>
          <span className={css.kicker}>{FABLE.painsEyebrow}</span>
          <h2 className={css.h2}>{FABLE.painsH}</h2>
          <p className={css.lede}>{FABLE.painsLede}</p>
        </div>
        <div className={css.painGrid}>
          {FABLE_PAINS.map(p => (
            <div className={css.pain} key={p.h}>
              <div className={css.painIc}><PainIcon name={p.ic} /></div>
              <div>
                <h4 className={css.painH}>{p.h}</h4>
                <p className={css.painP}>{p.p}</p>
              </div>
            </div>
          ))}
        </div>
        <p className={css.painTail}>{FABLE.painsTail}</p>
      </div>
    </section>

    {/* ── 10. OWNER RESOURCES ─────────────────────── */}
    <section className={css.sec} id="resources">
      <div className={css.wrap}>
        <div className={css.center}>
          <span className={css.kicker}>{FABLE.resEyebrow}</span>
          <h2 className={css.h2}>{FABLE.resH}</h2>
          <p className={css.lede}>{FABLE.resLede}</p>
        </div>
        <div className={css.resGrid}>
          {FABLE_GUIDES.map(g => (
            <div className={css.guide} key={g.h}>
              <img src={GUIDE_IMG[g.img]} alt={g.h} loading="lazy" />
              <div className={css.gBody}>
                <span className={css.gTag}>{g.tag}</span>
                <h4 className={css.gH}>{g.h}</h4>
                <p className={css.gP}>{g.p}</p>
              </div>
            </div>
          ))}
        </div>
        <div className={css.resList}>
          {FABLE_RESOURCE_CHIPS.map(c => <span key={c}>{c}</span>)}
        </div>
        <div className={css.newsletter}>
          <span className={css.kicker}>{FABLE.newsletterEyebrow}</span>
          <h3 className={css.newsletterH}>{FABLE.newsletterH}</h3>
          <p className={css.newsletterP}>{FABLE.newsletterP}</p>
          {newsletterLink(`${css.btn} ${css.btnRed} ${css.newsletterBtn}`, 'resources', FABLE.newsletterCta)}
        </div>
      </div>
    </section>

    {/* ── 11. DASHBOARD ───────────────────────────── */}
    <section className={css.dash}>
      <div className={css.wrap}>
        <div className={css.center}>
          <span className={css.kicker}>{FABLE.dashEyebrow}</span>
          <h2 className={css.h2}>{FABLE.dashH}</h2>
          <p className={css.lede}>{FABLE.dashLede}</p>
        </div>
        <div className={css.dashGrid}>
          {FABLE_STATS.map(s => (
            <div className={css.stat} key={s.lbl}>
              <div className={css.statV}>{s.num}</div>
              <div className={css.statL}>{s.lbl}</div>
              <div className={css.statD} />
            </div>
          ))}
        </div>
        <div className={css.dashCta}>
          {listFreeLink(`${css.dashCtaLink} cta-list-free`, 'dashboard', FABLE.dashCta)}
        </div>
      </div>
    </section>

    {/* ── 12. FAQ ─────────────────────────────────── */}
    <section className={css.sec} id="faq">
      <div className={css.wrap}>
        <div className={css.center}>
          <span className={css.kicker}>{FABLE.faqEyebrow}</span>
          <h2 className={css.h2}>{FABLE.faqH}</h2>
        </div>
        <div className={css.faqList}>
          {FAQ.map((f, i) => (
            <details key={f.q} className={css.faqItem} open={i === 0}>
              <summary className={css.faqQ}>{f.q}</summary>
              <div className={css.faqA}>{f.a}</div>
            </details>
          ))}
        </div>
      </div>
    </section>

    {/* ── 13. HAUL STAR CLOSE (black band) ────────── */}
    <section className={css.hsBand} id="haulstar">
      <div className={`${css.wrap} ${css.hsGrid}`}>
        <div>
          <span className={css.kicker}>{FABLE.hsEyebrow}</span>
          <h2 className={css.h2Inv}>{FABLE.hsH}</h2>
          <p className={css.hsSub}>{FABLE.hsLede}</p>
          <p className={css.hsCloseLine}>
            {FABLE.hsCloseLine}<em>{FABLE.hsCloseLineEm}</em>
          </p>
          <div className={css.btnRow}>
            {haulStarLink(`${css.btn} ${css.btnRed} cta-haul-star`, 'close', COPY.ctaBecomeHaulStar)}
          </div>
          <p className={css.hsSecondary}>
            {FABLE.hsSecondaryA}
            {listFreeLink(`${css.hsSecondaryLink} cta-list-free`, 'close', FABLE.hsSecondaryLink)}
          </p>
        </div>
        <div className={css.hsOffer}>
          <div className={css.hsPriceLine}>
            <span className={css.hsP}>{FABLE.hsPrice}</span>
            <span className={css.hsPer}>{FABLE.hsPer}</span>
          </div>
          <p className={css.hsNotPer}>{FABLE.hsNotPer}</p>
          <ul className={css.hsOfferList}>
            {FABLE.hsOfferList.map(o => (
              <li key={o}><Check stroke={RED} /> {o}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    {/* ── 14. FINAL CTA (dark image band) ─────────── */}
    <section className={css.final}>
      <div className={css.finalBg} style={{ backgroundImage: `url(${carHaulerImg})` }} aria-hidden="true" />
      <div className={css.finalOverlay} aria-hidden="true" />
      <div className={css.wrap}>
        <h2 className={css.finalH}>{FABLE.finalH}</h2>
        <p className={css.finalTag}>{FABLE.finalTag}</p>
        <div className={css.btnRow}>
          {listFreeLink(`${css.btn} ${css.btnRed} cta-list-free`, 'final', COPY.ctaListFree)}
          {haulStarLink(`${css.btn} ${css.btnWhite} cta-haul-star`, 'final', COPY.ctaHaulStarStart)}
        </div>
      </div>
    </section>

  </div>
);

FableLayout.propTypes = {
  listFreeLink: func.isRequired,
  haulStarLink: func.isRequired,
  newsletterLink: func.isRequired,
};

export default FableLayout;
